<template>
  <div class="flex flex-col gap-4 relative z-10">
    <div class="flex flex-wrap gap-2">
      <LotsShowInsuranceBadge
        v-if="hasInsurance"
      />

      <div
        v-if="showSafeDeal"
        class="inline-flex items-center gap-1 p-1 rounded-lg bg-gray-200"
      >
        <ProofsSafeDealIcon
          class="size-5 text-green-600 shrink-0"
          aria-hidden="true"
        />
        <span class="text-xs leading-3 font-bold text-gray-900 whitespace-nowrap">
          Безопасная сделка
        </span>
      </div>

      <div
        v-if="showFreeCancel"
        class="inline-flex items-center gap-1 p-1 rounded-lg bg-gray-200"
      >
        <ProofsFreeCancelIcon
          class="size-5 text-green-600 shrink-0"
          aria-hidden="true"
        />
        <span class="text-xs leading-3 font-bold text-gray-900 whitespace-nowrap">
          Бесплатная отмена
        </span>
      </div>

      <div
        v-if="showDepositChip"
        class="inline-flex items-center gap-1 p-1 rounded-lg bg-gray-200"
      >
        <ProofsDepositIcon
          v-if="lot.deposit"
          class="size-5 text-yellow-500 shrink-0"
          aria-hidden="true"
        />
        <ProofsNoDepositIcon
          v-if="!lot.deposit"
          class="size-5 text-green-600 shrink-0"
          aria-hidden="true"
        />
        <span class="text-xs leading-3 font-bold text-gray-900 whitespace-nowrap">
          <template v-if="lot.deposit">Залог {{ lot.deposit }}&nbsp;₽</template>
          <template v-if="!lot.deposit">Без залога</template>
        </span>
      </div>

      <div
        v-if="showDelivery"
        class="inline-flex items-center gap-1 p-1 rounded-lg bg-gray-200"
      >
        <ProofsDeliveryIcon
          class="size-5 text-green-600 shrink-0"
          aria-hidden="true"
        />
        <span class="text-xs leading-3 font-bold text-gray-900 whitespace-nowrap">
          {{ deliveryLabel }}
        </span>
      </div>
    </div>

    <div
      v-if="showPickupBlock"
      class="flex flex-col gap-1 px-3 py-2 rounded-lg bg-gray-200 w-full lg:hidden"
    >
      <div class="flex gap-1 items-center">
        <MapMarketLineIcon
          class="size-4 text-green-500 shrink-0"
          aria-hidden="true"
        />
        <span class="text-sm font-semibold text-gray-900">
          {{ pickupLabel }}
        </span>
      </div>

      <div
        v-if="!newVariant && lot.address"
        class="flex flex-wrap gap-1 items-center text-sm font-medium leading-5"
      >
        <span class="text-gray-600">{{ lot.address }}</span>
        <button
          type="button"
          class="text-blue-500 underline"
          @click="showMap"
        >
          Показать на карте
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import ProofsFreeCancelIcon from '~/assets/svg/ui/calendar-line.svg?component'
import ProofsNoDepositIcon from '~/assets/svg/ui/check-line.svg?component'
import ProofsDeliveryIcon from '~/assets/svg/ui/delivery-truck-line.svg?component'
import ProofsSafeDealIcon from '~/assets/svg/ui/lock-line.svg?component'
import MapMarketLineIcon from '~/assets/svg/ui/map-marker-line.svg?component'
import ProofsDepositIcon from '~/assets/svg/ui/wallet-line.svg?component'
import { useLot } from '~/composables/useLot'

const props = defineProps({
  newVariant: {
    type: Boolean,
    default: false,
  },
  isBooking: {
    type: Boolean,
    default: false,
  },
  showSafeDeal: {
    type: Boolean,
    default: true,
  },
  showPickup: {
    type: Boolean,
    default: false,
  },
  lotId: {
    type: [String, Number],
    required: true,
  },
})

const { $modal } = useNuxtApp()
const config = useRuntimeConfig()
const { lot } = useLot(props.lotId)

const isElka = computed(() => {
  return (
    config.public.elka?.includes(Number(lot.value.id))
    || Number(lot.value.id) === Number(config.public.elka)
  )
})

const hasInsurance = computed(() => Boolean(lot.value?.category?.insurance))

const showFreeCancel = computed(() => props.showSafeDeal && !props.isBooking)

const showDepositChip = computed(() => !props.newVariant)

const showDelivery = computed(() => props.showSafeDeal && !props.isBooking)

const showPickupBlock = computed(() => {
  return props.showPickup && !isElka.value
})

const pickupLabel = computed(() => {
  const equipmentAndVehicles = Boolean(lot.value?.category?.equipmentAndVehicles)
  return equipmentAndVehicles ? 'Адрес:' : 'Самовывоз:'
})

const deliveryLabel = computed(() => {
  const lotId = lot.value?.id

  if (config.public.isProduction && lotId == 45933) {
    return 'Бесплатная доставка в пределах МКАД'
  }
  if (config.public.isProduction && lotId == 61749) {
    return 'Бесплатная доставка в пределах КАД'
  }
  if (!config.public.isProduction && lotId == 41527) {
    return 'Бесплатная доставка в пределах МКАД'
  }
  if (props.newVariant) {
    return 'Доставка'
  }
  if (lot.value?.delivery === 'FREE_OWNER_DELIVERY') {
    return 'Бесплатная доставка'
  }
  if (lot.value?.delivery === 'PAID_OWNER_DELIVERY') {
    return 'Доставка по договорённости'
  }
  if (lot.value?.deliveryPrice === 0) {
    return 'Бесплатная доставка'
  }
  return `Доставка от ${lot.value?.deliveryPrice}\u00a0₽`
})

const showMap = () => {
  $modal.show('shared/modal/map', {
    location: lot.value.location,
    address: lot.value.address,
    analytics: true,
    lot: lot.value,
    owner: lot.value.user,
    ownerCity: lot.value.city.name,
  })
}
</script>
